/**
 * Plot controls. The left-panel card shown on the Plot tab:
 * a plot-type grid (line / scatter / histogram) and the type-specific inputs -
 * X picker + Y series, scatter color-by + scale, histogram bins - plus overlay
 * toggles, style toggles, axis bounds, and the Plot button.
 *
 * Everything writes the `plotState` atom through `state/plot-state.ts`; the
 * series lists reuse `<series-picker>` and axis bounds `<axis-bounds>`. The
 * actual fetch+draw (`doPlot`, `rerenderChartFromLast`, the fit popover) are
 * §10.8 seams. `exportPlottedSeries` (Plot → Export hand-off) works today since
 * it only touches the column selection.
 */

import { html, nothing, svg, type SVGTemplateResult, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeDataSource,
  selectedShip,
  allColumns,
  derivedSignals,
} from '../../state/store.ts';
import {
  plotState,
  setPlotType,
  setXColumn,
  setTimeAxis,
  isTimeAxis,
  timeColumn,
  addSeries,
  removeSeries,
  reorderSeries,
  clearAllSeries,
  setCustomColor,
  setColorBy,
  clearColorBy,
  setColorScale,
  setLatColumn,
  setLonColumn,
  setMapNautical,
  setMapShowTrack,
  addHistCol,
  removeHistCol,
  reorderHistCol,
  addCorrCol,
  removeCorrCol,
  reorderCorrCol,
  setCorrMethod,
  setCorrOrder,
  CORR_METHODS,
  CORR_MAX_COLUMNS,
  setPieMode,
  setBoxMode,
  addBoxCol,
  removeBoxCol,
  reorderBoxCol,
  setBoxColumn,
  setBoxGroupColumn,
  BOX_MAX_COLUMNS,
  setNumberField,
  addPieCol,
  removePieCol,
  reorderPieCol,
  setPieAgg,
  setPieBandColumn,
  setPieBands,
  setPieBandEdges,
  parseBandEdges,
  PIE_AGGS,
  PIE_MAX_COLUMNS,
  PIE_BANDS_MIN,
  PIE_BANDS_MAX,
  setHistBins,
  setShowLegs,
  setShowChangeovers,
  setYColumn,
  setZColumn,
  setZAgg,
  setHeatBins,
  HEAT_CELL_MODES,
  HEAT_BINS_MIN,
  HEAT_BINS_MAX,
  HEAT_BINS_STEP,
  HEAT_BINS_DEFAULT,
  setHeatStep,
  HIST_BINS_MIN,
  HIST_BINS_MAX,
  axisBoundsActiveCount,
  type PlotState,
} from '../../state/plot-state.ts';
import { doPlot, openFitPopover } from './plot-actions.ts';
import {
  fitLabels,
  lastPlotData,
  legsLoading,
  changeoversLoading,
} from './plot-ui-state.ts';
import {
  COLOR_SCALE_OPTIONS,
  DISCRETE_SCALE_OPTIONS,
  colorScaleGradientCss,
  seriesColor,
  seriesColorAt,
  PLOT_COLORS,
  DISCRETE_MAX_UNIQUE,
  CATEGORICAL_MAX_UNIQUE,
  isDiscreteScale,
  countUniqueUpTo,
} from './plot-constants.ts';
import { discreteColors } from './render/color-scale.ts';
import { plotColumnPool } from '../ship/ship-columns.ts';
import { fmtStep } from './render/format.ts';
import { isMultiShip } from '../../state/store.ts';
import { hasFlag, serverFlags } from '../../state/feature-flags.ts';
import { supportsGrid } from './multi-ship-plot.ts';
import { isNumericType, isTemporalType, wrapColName } from '../../utils/columns.ts';
import type {
  ColumnInfo,
  CorrMethod,
  CorrOrder,
  HeatmapAgg,
  HeatmapResponse,
  PieAgg,
  NumberAgg,
  PlotType,
} from '../../api/types.ts';
import './series-picker.ts';
import './axis-bounds.ts';
import './axis-titles.ts';
import './leg-search.ts';
import '../../ui/column-combobox.ts';
import '../../ui/plain-select.ts';
import '../../ui/unit-picker.ts';
import '../../ui/number-formula-editor.ts';
import './table-controls.ts';
import './gauge-controls.ts';
import './sankey-controls.ts';
import { isXY } from '../../api/plot-shapes.ts';
import { PLOT_TYPES, plotTypeIcon } from './plot-type-icons.ts';
import { periodComparison } from '../../state/period-comparison.ts';
import { NUMBER_AGGS } from './number-config.ts';

@customElement('plot-controls')
export class PlotControls extends LightElement {
  private store = new StoreController(this, [
    plotState,
    selectedShip,
    allColumns,
    derivedSignals,
    activeDataSource,
    fitLabels,
    lastPlotData,
    legsLoading,
    changeoversLoading,
    periodComparison,
    serverFlags,
  ]);

  private fitLabelOf = (name: string) => fitLabels.value[name];

  /**
   * The Plot card's "Advanced" disclosure. Collapsed until someone wants the
   * knobs: everything inside it - cell size, the leg and changeover bands, axis
   * bounds - has a working default, and none of it is part of describing the
   * chart you want.
   */
  @state() private advancedOpen = false;
  /** Why the typed pie cut points could not be used, or null when they were. */
  @state() private pieEdgeError: string | null = null;

  /* ── Column pools ─────────────────────────────────────── */

  /** Numeric raw + derived pseudo-columns (line/scatter Y, histogram). */
  private plottableNumeric(): ColumnInfo[] {
    return plotColumnPool().filter((c) => isNumericType(c.type) || c.derived);
  }

  /** Color-by pool: numeric/derived (continuous gradient) plus low-cardinality
   *  categorical text/bool columns (discrete swatches). Temporal and complex
   *  (list/struct) columns are excluded - they don't color meaningfully. */
  private colorByPool(): ColumnInfo[] {
    return plotColumnPool().filter(
      (c) =>
        c.derived ||
        isNumericType(c.type) ||
        /VARCHAR|STRING|TEXT|BOOL/i.test(c.type),
    );
  }

  /** True when `name` is a non-numeric color-by column (→ discrete only). */
  private isCategoricalCol(name: string): boolean {
    if (!name) return false;
    const col = allColumns.value.find((c) => c.name === name);
    return !!col && !isNumericType(col.type) && !isTemporalType(col.type);
  }

  private hasFit = (name: string): boolean =>
    plotState.value.fits.some((f) => f.series === name);

  /* ── Type grid ────────────────────────────────────────── */

  private renderTypeGrid(s: PlotState): TemplateResult {
    // Sankey is in development: only where its flag is on (administrators),
    // and then marked so nobody mistakes it for a finished type.
    const preview = hasFlag('sankey');
    const types = PLOT_TYPES.filter((pt) => pt.value !== 'sankey' || preview);
    // Comparing periods draws Line and Scatter only. The other types stay in
    // view, greyed and explained, rather than vanishing - or, as before,
    // quietly pausing the comparison when clicked.
    const comparing = !!periodComparison.value;
    return html`
      <div class="plot-type-grid">
        ${types.map((pt) => {
          const unavailable = comparing && pt.value !== 'line' && pt.value !== 'scatter';
          return html`
            <button
              class="plot-type-btn ${s.plot_type === pt.value ? 'active' : ''} ${unavailable ? 'is-unavailable' : ''}"
              data-tour=${pt.value === 'line' ? 'plot-type-line' : nothing}
              aria-disabled=${unavailable ? 'true' : 'false'}
              title=${unavailable
                ? `${pt.label} is not available while comparing periods - stop comparing to use it`
                : pt.value === 'sankey'
                  ? 'Sankey chart - administrators only while it is in development'
                  : `${pt.label} chart`}
              @click=${() => {
                if (!unavailable) setPlotType(pt.value);
              }}
            >
              ${plotTypeIcon(pt.value)}
              <span class="plot-type-name">${pt.label}</span>
              ${pt.value === 'sankey' ? html`<span class="admin-role-chip plot-type-chip">Admin</span>` : nothing}
            </button>
          `;
        })}
      </div>
    `;
  }

  /* ── Line inputs ──────────────────────────────────────── */

  private renderLineInputs(s: PlotState): TemplateResult {
    const numeric = this.plottableNumeric();
    return html`
      <div class="axis-block">
        <label>X axis</label>
        <column-combobox
          .columns=${plotColumnPool().filter(
            (c) => c.derived || isNumericType(c.type) || isTemporalType(c.type),
          )}
          .value=${s.x_column}
          placeholder="X column…"
          .select=${(name: string) => setXColumn(name)}
          .clear=${isTimeAxis(s) ? null : () => setTimeAxis()}
        ></column-combobox>
      </div>
      <div class="axis-block" data-tour="y-series">
        <label
          >Y axis
          ${s.y_left.length + s.y_right.length > 0
            ? html`<button
                class="link-btn label-action"
                title="Remove every selected series"
                @click=${() => clearAllSeries()}
              >
                Remove all
              </button>`
            : nothing}</label
        >
        <series-picker
          .items=${s.y_left}
          .pool=${numeric}
          addPlaceholder="+ Add left series…"
          .showColor=${true}
          .showFit=${true}
          .colorOf=${(name: string) => seriesColor(s, name)}
          .hasFit=${this.hasFit}
          .addItem=${(name: string) => addSeries('left', name)}
          .removeItem=${(name: string) => removeSeries('left', name)}
          .reorder=${(from: number, to: number) => reorderSeries('left', from, to)}
          .recolor=${(name: string, hex: string) => setCustomColor(name, hex)}
          .openFit=${(name: string, el: HTMLElement) => openFitPopover(name, el)}
          .fitLabelOf=${this.fitLabelOf}
        ></series-picker>
      </div>
      <div class="axis-block">
        <label>Secondary Y axis</label>
        <series-picker
          .items=${s.y_right}
          .pool=${numeric}
          addPlaceholder="+ Add right series…"
          .showColor=${true}
          .showFit=${true}
          .colorOf=${(name: string) => seriesColor(s, name)}
          .hasFit=${this.hasFit}
          .addItem=${(name: string) => addSeries('right', name)}
          .removeItem=${(name: string) => removeSeries('right', name)}
          .reorder=${(from: number, to: number) => reorderSeries('right', from, to)}
          .recolor=${(name: string, hex: string) => setCustomColor(name, hex)}
          .openFit=${(name: string, el: HTMLElement) => openFitPopover(name, el)}
          .fitLabelOf=${this.fitLabelOf}
        ></series-picker>
      </div>
      ${this.renderColorBy(s, [s.x_column])}
    `;
  }

  /* ── Scatter inputs ───────────────────────────────────── */

  private renderScatterInputs(s: PlotState): TemplateResult {
    const numeric = this.plottableNumeric();
    const xPool = plotColumnPool().filter(
      (c) => c.derived || isNumericType(c.type) || isTemporalType(c.type),
    );
    return html`
      <div class="axis-block">
        <label>X column</label>
        <column-combobox
          .columns=${xPool}
          .value=${s.x_column}
          placeholder="X column…"
          .select=${(name: string) => setXColumn(name)}
        ></column-combobox>
      </div>
      <div class="axis-block">
        <label>Y columns</label>
        <series-picker
          .items=${s.y_left}
          .pool=${numeric}
          .exclude=${[s.x_column]}
          addPlaceholder="+ Add Y column…"
          .showColor=${true}
          .showFit=${true}
          .colorOf=${(name: string, i: number) => seriesColorAt(s, name, i)}
          .hasFit=${this.hasFit}
          .addItem=${(name: string) => addSeries('left', name)}
          .removeItem=${(name: string) => removeSeries('left', name)}
          .reorder=${(from: number, to: number) => reorderSeries('left', from, to)}
          .recolor=${(name: string, hex: string) => setCustomColor(name, hex)}
          .openFit=${(name: string, el: HTMLElement) => openFitPopover(name, el)}
          .fitLabelOf=${this.fitLabelOf}
        ></series-picker>
      </div>
      <div class="axis-block">
        <label>Secondary Y axis</label>
        <!-- Palette indices continue past the left list, so a column keeps one
             colour whichever axis it is moved to. -->
        <series-picker
          .items=${s.y_right}
          .pool=${numeric}
          .exclude=${[s.x_column]}
          addPlaceholder="+ Add right column…"
          .showColor=${true}
          .showFit=${true}
          .colorOf=${(name: string, i: number) => seriesColorAt(s, name, s.y_left.length + i)}
          .hasFit=${this.hasFit}
          .addItem=${(name: string) => addSeries('right', name)}
          .removeItem=${(name: string) => removeSeries('right', name)}
          .reorder=${(from: number, to: number) => reorderSeries('right', from, to)}
          .recolor=${(name: string, hex: string) => setCustomColor(name, hex)}
          .openFit=${(name: string, el: HTMLElement) => openFitPopover(name, el)}
          .fitLabelOf=${this.fitLabelOf}
        ></series-picker>
      </div>
      ${this.renderColorBy(s, [s.x_column])}
    `;
  }

  /* ── Heatmap inputs ───────────────────────────────────── */

  /**
   * X and Y are both binned; Z is optional and decides what a cell means.
   * Without it the colour is density - how many rows landed in the cell, the
   * thing a saturated scatter cannot show. With it, the cell carries an
   * aggregate of that column instead ("mean SFOC across the SOG × draft
   * envelope").
   *
   * Bins cost a re-query, so they live here rather than in the gear; the colour
   * ramp and its scaling redraw from cache, so those are in the gear.
   */
  private renderHeatmapInputs(s: PlotState): TemplateResult {
    const numeric = this.plottableNumeric();
    // One "what is a cell" control, the way every other heatmap tool asks it:
    // the metric first, the column second. Splitting them into a column picker
    // plus a separate aggregate select made "count the rows" look like an
    // absent column rather than a choice.
    const mode = s.z_agg ?? 'density';
    const needsColumn = mode !== 'density' && !s.z_column;
    return html`
      <div class="axis-block">
        <label>X column</label>
        <column-combobox
          .columns=${numeric}
          .value=${s.x_column}
          .exclude=${[s.y_column]}
          placeholder="X column…"
          .select=${(name: string) => setXColumn(name)}
        ></column-combobox>
      </div>
      <div class="axis-block">
        <label>Y column</label>
        <column-combobox
          .columns=${numeric}
          .value=${s.y_column}
          .exclude=${[s.x_column]}
          placeholder="Y column…"
          .select=${(name: string) => setYColumn(name)}
          .clear=${s.y_column ? () => setYColumn('') : null}
        ></column-combobox>
      </div>
      <div class="axis-block">
        <label>Cell value</label>
        <plain-select
          id="heat-cell-value"
          .value=${mode}
          .options=${HEAT_CELL_MODES.map((m) => ({ value: m.value, label: m.label }))}
          .select=${(v: string) => setZAgg(v === 'density' ? null : (v as HeatmapAgg))}
        ></plain-select>
        ${mode === 'density'
          ? nothing
          : html`<column-combobox
                .columns=${numeric}
                .value=${s.z_column}
                .exclude=${[s.x_column, s.y_column]}
                placeholder="of column…"
                .select=${(name: string) => setZColumn(name)}
                .clear=${s.z_column ? () => setZColumn('') : null}
              ></column-combobox>
              ${needsColumn
                ? html`<div class="toggle-hint">
                    Pick a column, or cells stay coloured by row count.
                  </div>`
                : nothing}`}
      </div>
      <div class="axis-block">
        <label
          >Colour
          <span class="toggle-hint">${s.color_scale}</span></label
        >
        ${this.renderRampSwatches(s)}
      </div>
      ${this.renderAdvanced(s)}
    `;
  }

  /**
   * Cell size and resolution, behind a disclosure.
   *
   * Everything here has a working automatic answer - the server derives the
   * cell size from the data's own spread - so the default path should not ask
   * about it at all. The header carries the override when there is one, so a
   * collapsed section never hides a setting that is doing something.
   *
   * Same shape as Axis bounds below it, deliberately: two disclosures that
   * behave alike read as one idea.
   */
  private renderAdvanced(s: PlotState): TemplateResult {
    if (s.plot_type === 'histogram' || s.plot_type === 'map') return html`${nothing}`;
    if (s.plot_type === 'correlation' || s.plot_type === 'pie' || s.plot_type === 'number' || s.plot_type === 'gauge' || s.plot_type === 'sankey') return html`${nothing}`;
    const open = this.advancedOpen;
    const heat = s.plot_type === 'heatmap';
    const overrides: string[] = [];
    if (heat && (s.heat_x_step != null || s.heat_y_step != null || s.heat_bins != null)) {
      overrides.push(this.cellSizeHint(s));
    }
    if (s.plot_type === 'line') {
      if (s.show_legs) overrides.push('legs');
      if (s.show_changeovers) overrides.push('changeovers');
    }
    const bounds = axisBoundsActiveCount(s);
    if (bounds) overrides.push(`${bounds} bound${bounds === 1 ? '' : 's'}`);
    return html`
      <div class="axis-bounds-wrap">
        <div class="section-header" @click=${() => (this.advancedOpen = !this.advancedOpen)}>
          <span class="arrow ${open ? 'open' : ''}">▶</span>
          <span>Advanced</span>
          <span class="count">${overrides.length ? overrides.join(' · ') : 'auto'}</span>
        </div>
        <div class="section-body ${open ? '' : 'collapsed'}">
          ${heat
            ? html`<div class="axis-block">
                <label
                  >Cell size
                  <span class="toggle-hint">${this.cellSizeHint(s)}</span></label
                >
                ${this.renderCellSize(s)}
              </div>`
            : nothing}
          ${this.renderOverlays(s)}
          <axis-bounds ?embedded=${true}></axis-bounds>
        </div>
      </div>
    `;
  }

  /**
   * The common ramps as gradient chips. A heatmap is its colour in a way a line
   * chart is not, so the choice sits with the inputs rather than behind the
   * gear — where the full list, and the ramp's scaling, still live.
   */
  private renderRampSwatches(s: PlotState): TemplateResult {
    return html`
      <div class="ramp-swatches">
        ${COLOR_SCALE_OPTIONS.map(
          (o) => html`<button
            type="button"
            class="ramp-swatch ${s.color_scale === o.value ? 'active' : ''}"
            style=${`background:${colorScaleGradientCss(o.value)}`}
            title=${o.label}
            aria-label=${o.label}
            aria-pressed=${s.color_scale === o.value ? 'true' : 'false'}
            @click=${() => setColorScale(o.value)}
          ></button>`,
        )}
      </div>
    `;
  }

  /**
   * Bin count: drag for a coarse change, arrows or the buttons for a precise
   * one. The slider alone only answered arrow keys once it had focus, which is
   * not discoverable - the buttons make the same steps clickable, and the whole
   * row takes the keys.
   */
  /** The grid the last draw actually used, when it is a heatmap's. */
  private drawnGrid(): HeatmapResponse | null {
    const d = lastPlotData.value;
    return d && d.plot_type === 'heatmap' ? d : null;
  }

  /**
   * What the cells actually came out as.
   *
   * A bin count is only ever a proxy for cell size, and the server rounds the
   * width it implies up to a nice step - so what was asked for and what was
   * drawn routinely differ. Showing the real figure is what keeps the control
   * honest.
   */
  private cellSizeHint(s: PlotState): string {
    const drawn = this.drawnGrid();
    if (!drawn?.x_step || !drawn.y_step) return s.heat_bins ? '' : 'auto';
    const auto = !s.heat_x_step && !s.heat_y_step && !s.heat_bins ? ' auto' : '';
    return `${fmtStep(drawn.x_step)} x ${fmtStep(drawn.y_step)}${auto} - ${drawn.nx}x${drawn.ny}`;
  }

  /**
   * Cell size in data units, per axis - the control Tableau and Grafana put
   * first, because "half a knot by a hundredth of a degree" is the question
   * actually being asked. Empty hands the axis back to a derived width, with
   * the drawn step as the placeholder so an empty box reads as "what the data
   * suggests" rather than as nothing.
   */
  private renderCellSize(s: PlotState): TemplateResult {
    const drawn = this.drawnGrid();
    const box = (
      axis: 'x' | 'y',
      label: string,
      value: number | null,
      placeholder: number | null | undefined,
    ): TemplateResult => html`
      <span class="cell-size-field">
        <span class="cell-size-axis">${label}</span>
        <input
          type="number"
          min="0"
          step="any"
          class="cell-size-input"
          placeholder=${placeholder ? fmtStep(placeholder) : 'auto'}
          .value=${value == null ? '' : String(value)}
          title=${`Cell size along ${label}. Leave empty to follow the data.`}
          @change=${(e: Event) => {
            const raw = (e.target as HTMLInputElement).value.trim();
            setHeatStep(axis, raw === '' ? null : parseFloat(raw));
          }}
        />
      </span>
    `;
    return html`
      <div class="cell-size-row">
        ${box('x', 'X', s.heat_x_step, drawn?.x_step)}
        ${box('y', 'Y', s.heat_y_step, drawn?.y_step)}
      </div>
      ${s.heat_x_step && s.heat_y_step ? nothing : this.renderBinsControl(s)}
    `;
  }

  private renderBinsControl(s: PlotState): TemplateResult {
    const auto = s.heat_bins == null;
    const bins = s.heat_bins ?? HEAT_BINS_DEFAULT;
    const step = (by: number): void => setHeatBins(bins + by);
    return html`
      <div
        class="heat-bins-row"
        role="group"
        aria-label="Heatmap bins per axis"
        @keydown=${(e: KeyboardEvent) => {
          // The row listens, but is not itself focusable — a focusable wrapper
          // drew a box around the whole control the moment it was clicked. The
          // keys arrive by bubbling from whichever button has focus; the slider
          // is skipped because it steps itself natively, and handling it here
          // too would move two notches per press.
          if ((e.target as HTMLElement).tagName === 'INPUT') return;
          const by =
            e.key === 'ArrowUp' || e.key === 'ArrowRight'
              ? HEAT_BINS_STEP
              : e.key === 'ArrowDown' || e.key === 'ArrowLeft'
                ? -HEAT_BINS_STEP
                : 0;
          if (!by) return;
          e.preventDefault();
          step(by);
        }}
      >
        <button
          type="button"
          class="chart-series-btn heat-auto-btn ${auto ? 'active' : ''}"
          title="Derive the cell size from the data's own spread"
          @click=${() => setHeatBins(auto ? HEAT_BINS_DEFAULT : null)}
        >
          Auto
        </button>
        <button
          type="button"
          class="stepper-btn"
          title="Coarser cells"
          ?disabled=${auto || bins <= HEAT_BINS_MIN}
          @click=${() => step(-HEAT_BINS_STEP)}
        >
          −
        </button>
        <input
          type="range"
          min=${HEAT_BINS_MIN}
          max=${HEAT_BINS_MAX}
          step=${HEAT_BINS_STEP}
          ?disabled=${auto}
          .value=${String(bins)}
          aria-label="Bins per axis"
          @change=${(e: Event) =>
            setHeatBins(parseInt((e.target as HTMLInputElement).value, 10))}
        />
        <button
          type="button"
          class="stepper-btn"
          title="Finer cells"
          ?disabled=${auto || bins >= HEAT_BINS_MAX}
          @click=${() => step(HEAT_BINS_STEP)}
        >
          +
        </button>
        <span class="mono-val">${auto ? 'auto' : `~${bins}`}</span>
      </div>
    `;
  }

  /* ── Color-by + scale (shared by scatter and map) ─────── */

  /**
   * The "Color by" column picker + color-scale select. Shared by scatter and
   * map. `exclude` drops columns that already have another role (the scatter X,
   * or the map lat/lon) from the color-by pool.
   */
  private renderColorBy(s: PlotState, exclude: string[]): TemplateResult {
    const categorical = this.isCategoricalCol(s.color_by);
    // We only know the true cardinality once a /plot returns `color_values`.
    const cap = categorical ? CATEGORICAL_MAX_UNIQUE : DISCRETE_MAX_UNIQUE;
    const drawn = lastPlotData.value;
    const plottedColorVals =
      drawn && isXY(drawn) && drawn.color_by === s.color_by
        ? drawn.color_values
        : null;
    const uniqueCount = plottedColorVals ? countUniqueUpTo(plottedColorVals, cap) : 0;
    const isDiscrete = isDiscreteScale(s.color_scale);
    // Numeric high-cardinality columns hide discrete (a gradient reads better).
    // Categorical columns always offer discrete - there's no gradient fallback;
    // if they exceed the cap the backend drops the coloring with a warning.
    const numericTooMany = !categorical && uniqueCount >= DISCRETE_MAX_UNIQUE;
    const offerDiscrete = !!s.color_by && (categorical || !numericTooMany || isDiscrete);
    const discreteLabel = uniqueCount > 0 ? `Discrete (${uniqueCount} values)` : 'Discrete';
    const swatchCount = Math.min(uniqueCount || PLOT_COLORS.length, PLOT_COLORS.length);
    const swatches = discreteColors(s.color_scale, swatchCount);
    const excludeSet = new Set([...exclude, s.color_by]);
    // Colouring points by a third column and colouring them by ship are the
    // same channel, so they cannot both be on. On one shared chart the ships
    // win. Grid gives each ship its own panel, which frees the channel again -
    // the same rule the leg and changeover bands follow.
    const gridded = s.view_mode === 'grid' && supportsGrid(s.plot_type);
    const multi = isMultiShip() && !gridded;
    // A line colours its segments, a scatter its points; the control is the
    // same, so say which it will be rather than leaving it to be discovered.
    const what = s.plot_type === 'line' ? 'line' : 'points';
    return html`
      <div class="axis-block ${multi ? 'unavailable' : ''}">
        <label
          title=${multi
            ? `The ${what} are coloured by ship while several share one chart - switch to Grid to colour by a column instead`
            : `Colours the ${what} by a third column`}
          >Color by (optional)${multi
            ? html` <span class="toggle-hint">one ship, or Grid</span>`
            : nothing}</label
        >
        ${s.color_by && !multi
          ? // Chosen: the chip *replaces* the picker rather than sitting above
            // it. One column can be the colour channel, so a search box left
            // open under the answer is an input with nothing left to do - and
            // it pushed everything below it down for no reason. Clearing the
            // chip brings the picker back.
            html`<div class="series-chip color-by-chip" style="--chip-color: var(--accent)">
              <span class="chip-name">${wrapColName(s.color_by)}</span>
              <button class="chip-remove" title="Clear" @click=${() => clearColorBy()}>×</button>
            </div>`
          : html`<column-combobox
              .columns=${this.colorByPool().filter((c) => !excludeSet.has(c.name))}
              .value=${''}
              ?disabled=${multi}
              placeholder=${multi ? 'Coloured by ship' : 'None (fixed colors)'}
              .select=${(name: string) => name && setColorBy(name)}
            ></column-combobox>`}
      </div>
      ${s.color_by && !multi
        ? html`<div class="axis-block">
            <label>Color scale</label>
            ${this.renderScalePicker(s, { offerDiscrete, categorical, discreteLabel, swatchCount })}
          </div>`
        : nothing}
    `;
  }

  /**
   * The colour scale, as swatches rather than a dropdown.
   *
   * The heatmap has always shown its ramps this way, for a reason that applies
   * just as well here: a palette is a thing you recognise, not a name you read.
   * "Turbo" and "Plasma" mean nothing side by side in a `<select>`; the two
   * strips of colour are instantly different.
   *
   * Two groups, because the choice is really two questions. **Discrete** gives
   * every value its own colour - what you want for a handful of categories, and
   * the swatch shows the actual colours those values will get. **Gradient** maps
   * a number onto a ramp, which needs the values to be continuous. Which groups
   * appear is not cosmetic: a categorical column has no gradient reading, and a
   * numeric column with more distinct values than there are colours cannot be
   * told apart discretely.
   */
  private renderScalePicker(
    s: PlotState,
    opts: {
      offerDiscrete: boolean;
      categorical: boolean;
      discreteLabel: string;
      swatchCount: number;
    },
  ): TemplateResult {
    const { offerDiscrete, categorical, discreteLabel, swatchCount } = opts;
    return html`
      <div class="scale-picker">
        ${offerDiscrete
          ? html`<div class="scale-group">
              <span class="scale-group-label">${discreteLabel}</span>
              <div class="ramp-swatches">
                ${DISCRETE_SCALE_OPTIONS.map((o) => {
                  const colors = discreteColors(o.value, swatchCount);
                  return html`<button
                    type="button"
                    class="ramp-swatch is-discrete ${s.color_scale === o.value ? 'active' : ''}"
                    title=${o.label}
                    aria-label=${`${o.label} (discrete)`}
                    aria-pressed=${s.color_scale === o.value ? 'true' : 'false'}
                    @click=${() => setColorScale(o.value)}
                  >
                    ${colors.map(
                      (c) => html`<span class="cs-swatch" style=${`background:${c}`}></span>`,
                    )}
                  </button>`;
                })}
              </div>
            </div>`
          : nothing}
        ${categorical
          ? nothing
          : html`<div class="scale-group">
              <span class="scale-group-label">Gradient</span>
              <div class="ramp-swatches">
                ${COLOR_SCALE_OPTIONS.map(
                  (o) => html`<button
                    type="button"
                    class="ramp-swatch ${s.color_scale === o.value ? 'active' : ''}"
                    style=${`background:${colorScaleGradientCss(o.value)}`}
                    title=${o.label}
                    aria-label=${`${o.label} (gradient)`}
                    aria-pressed=${s.color_scale === o.value ? 'true' : 'false'}
                    @click=${() => setColorScale(o.value)}
                  ></button>`,
                )}
              </div>
            </div>`}
      </div>
    `;
  }

  /* ── Map inputs ───────────────────────────────────────── */

  private renderMapInputs(s: PlotState): TemplateResult {
    const numeric = this.plottableNumeric();
    return html`
      <div class="axis-block">
        <label>Latitude column</label>
        <column-combobox
          .columns=${numeric}
          .value=${s.lat_column}
          placeholder="Latitude column…"
          .select=${(name: string) => setLatColumn(name)}
        ></column-combobox>
      </div>
      <div class="axis-block">
        <label>Longitude column</label>
        <column-combobox
          .columns=${numeric}
          .value=${s.lon_column}
          placeholder="Longitude column…"
          .select=${(name: string) => setLonColumn(name)}
        ></column-combobox>
      </div>
      ${this.renderColorBy(s, [s.lat_column, s.lon_column])}
      <div class="map-toggles">
        ${this.mapToggle('Track line', s.map_show_track, setMapShowTrack,
          'Points are joined in time order.', 'Points only, no line.')}
        ${this.mapToggle('Nautical chart', s.map_nautical, setMapNautical,
          'Seamarks, buoys and lights on the map.', 'Plain basemap.')}
      </div>
    `;
  }

  /** The /ui whole-row switch (.dset-toggle): the line under the label says
   *  what the current state means, not what the switch is. */
  private mapToggle(label: string, on: boolean, set: (v: boolean) => void, onText: string, offText: string): TemplateResult {
    return html`<button
      type="button"
      class="dset-toggle ${on ? 'is-on' : ''}"
      role="switch"
      aria-checked=${on ? 'true' : 'false'}
      @click=${() => set(!on)}
    >
      <span class="dset-toggle-text">
        <strong>${label}</strong>
        <span>${on ? onText : offText}</span>
      </span>
      <span class="sd-switch" aria-hidden="true"></span>
    </button>`;
  }

  /* ── Correlation inputs ───────────────────────────────── */

  /**
   * The signals to measure against each other, plus how.
   *
   * The same picker the histogram uses: order matters here in a way it does not
   * there - it is the axis order of the matrix - and the picker already drags to
   * reorder, so arranging the matrix by hand costs nothing extra. `Arrange`
   * hands that job to the server instead, which sorts by similarity so blocks
   * become visible.
   *
   * Both controls cost a re-query, so they sit with the inputs rather than
   * behind the gear; the ramp and the triangle toggle redraw from cache and live
   * in the gear with the other style.
   */
  private renderCorrelationInputs(s: PlotState): TemplateResult {
    const numeric = this.plottableNumeric();
    const count = s.corr_columns.length;
    const full = count >= CORR_MAX_COLUMNS;
    return html`
      <div class="axis-block">
        <label
          >Signals
          <span class="toggle-hint"
            >${count < 2 ? 'two or more' : `${count} · ${count * (count - 1) / 2} pairs`}</span
          ></label
        >
        <series-picker
          .items=${s.corr_columns}
          .pool=${numeric}
          addPlaceholder=${full ? `Limit is ${CORR_MAX_COLUMNS} signals` : '+ Add signal…'}
          .addItem=${(name: string) => addCorrCol(name)}
          .removeItem=${(name: string) => removeCorrCol(name)}
          .reorder=${(from: number, to: number) => reorderCorrCol(from, to)}
        ></series-picker>
      </div>
      <div class="axis-block">
        <label>Coefficient</label>
        <plain-select
          id="corr-method"
          .value=${s.corr_method}
          .options=${CORR_METHODS.map((m) => ({ value: m.value, label: m.label }))}
          .select=${(v: string) => setCorrMethod(v as CorrMethod)}
        ></plain-select>
        <div class="toggle-hint">
          ${s.corr_method === 'pearson'
            ? 'Straight-line relationships only - a curve can score near zero.'
            : 'Ranks, so any consistent direction counts. Slower on a wide range.'}
        </div>
      </div>
      <div class="axis-block">
        <label>Arrange</label>
        <plain-select
          id="corr-order"
          .value=${s.corr_order}
          .options=${[
            { value: 'given', label: 'As picked' },
            { value: 'cluster', label: 'By similarity' },
          ]}
          .select=${(v: string) => setCorrOrder(v as CorrOrder)}
        ></plain-select>
      </div>
      <div class="axis-block">
        <label
          >Colour
          <span class="toggle-hint">${s.color_scale}</span></label
        >
        ${this.renderRampSwatches(s)}
      </div>
    `;
  }

  /* ── Number inputs ────────────────────────────────────── */
  private renderNumberInputs(s: PlotState): TemplateResult {
    const formula = s.number_mode === 'formula';
    return html`
      <div class="axis-block">
        <label>Value</label>
        <div class="seg">
          <button class="seg-btn ${formula ? '' : 'active'}" aria-pressed=${!formula}
            @click=${() => setNumberField('number_mode', 'signal')}>Signal</button>
          <button class="seg-btn ${formula ? 'active' : ''}" aria-pressed=${formula}
            @click=${() => setNumberField('number_mode', 'formula')}>Calculation</button>
        </div>
      </div>
      ${formula ? html`
        <div class="axis-block">
          <number-formula-editor .value=${s.number_expression} .columns=${this.plottableNumeric()}
            .change=${(value: string) => setNumberField('number_expression', value)}></number-formula-editor>
        </div>` : html`
        <div class="axis-block">
          <label>Signal</label>
          <column-combobox id="number-signal" .columns=${this.plottableNumeric()} .value=${s.number_column}
            placeholder="Choose signal…" .select=${(v: string) => setNumberField('number_column', v)}></column-combobox>
        </div>
        <div class="axis-block">
          <label>Calculation</label>
          <plain-select id="number-agg" .value=${s.number_agg} .options=${NUMBER_AGGS}
            .select=${(v: string) => setNumberField('number_agg', v as NumberAgg)}></plain-select>
          <div class="toggle-hint">${s.number_agg === 'latest'
            ? 'Last numeric value by timestamp in the selected range.'
            : s.number_agg === 'sum' ? 'Adds numeric samples; rates are not integrated over time.'
            : 'Uses all matching numeric values in the selected range.'}</div>
        </div>`}
      <div class="axis-block">
        <label for="number-label">Label</label>
        <input type="text" id="number-label" maxlength="120" placeholder="No label"
          .value=${s.number_label} @input=${(e: Event) => setNumberField('number_label', (e.target as HTMLInputElement).value)} />
      </div>
      <div class="axis-block">
        <label>Unit</label>
        <unit-picker id="number-unit" .value=${s.number_unit}
          .select=${(v: string) => setNumberField('number_unit', v)}></unit-picker>
      </div>
      <div class="axis-block">
        <label for="number-decimals">Decimal places</label>
        <plain-select id="number-decimals" .value=${String(s.number_decimals)}
          .options=${Array.from({length: 11}, (_, i) => ({value: String(i), label: String(i)}))}
          .select=${(v: string) => setNumberField('number_decimals', Number(v))}></plain-select>
      </div>
    `;
  }

  /* ── Box plot inputs ──────────────────────────────────── */

  /**
   * Two readings of "how spread out", behind one switch, like the pie's.
   *
   * By signal: one box per signal, in picker order, and dragging the picker
   * redraws without a query. By group: one signal, split by the values of a
   * column - categories, or a signal with few distinct values.
   */
  private renderBoxInputs(s: PlotState): TemplateResult {
    const groups = s.box_mode === 'groups';
    return html`
      <div class="axis-block">
        <label>Boxes</label>
        <div class="seg">
          <button
            type="button"
            class="seg-btn ${groups ? '' : 'active'}"
            aria-pressed=${groups ? 'false' : 'true'}
            title="One box per signal - how their spreads compare"
            @click=${() => setBoxMode('signals')}
          >
            By signal
          </button>
          <button
            type="button"
            class="seg-btn ${groups ? 'active' : ''}"
            aria-pressed=${groups ? 'true' : 'false'}
            title="One signal split by the values of a column - load by fuel, speed by mode"
            @click=${() => setBoxMode('groups')}
          >
            By group
          </button>
        </div>
      </div>
      ${groups ? this.renderBoxGroupInputs(s) : this.renderBoxSignalInputs(s)}
      <div class="toggle-hint" title="Whiskers reach the furthest reading within 1.5 × IQR of the box; readings beyond them are drawn as outliers.">
        Whiskers 1.5 × IQR · dots are outliers
      </div>
    `;
  }

  private renderBoxSignalInputs(s: PlotState): TemplateResult {
    const count = s.box_columns.length;
    const full = count >= BOX_MAX_COLUMNS;
    return html`
      <div class="axis-block">
        <label
          >Signals
          <span class="toggle-hint">${count ? `${count} ${count === 1 ? 'box' : 'boxes'}` : 'one or more'}</span></label
        >
        <series-picker
          .items=${s.box_columns}
          .pool=${this.plottableNumeric()}
          addPlaceholder=${full ? `Limit is ${BOX_MAX_COLUMNS} boxes` : '+ Add signal…'}
          .colorOf=${(name: string, i: number) => seriesColorAt(s, name, i)}
          .addItem=${(name: string) => addBoxCol(name)}
          .removeItem=${(name: string) => removeBoxCol(name)}
          .reorder=${(from: number, to: number) => reorderBoxCol(from, to)}
        ></series-picker>
      </div>
    `;
  }

  private renderBoxGroupInputs(s: PlotState): TemplateResult {
    return html`
      <div class="axis-block">
        <label>Signal</label>
        <column-combobox
          .columns=${this.plottableNumeric().filter((c) => c.name !== s.box_group_column)}
          .value=${s.box_column}
          placeholder="Signal to summarise…"
          .select=${(name: string) => setBoxColumn(name)}
        ></column-combobox>
      </div>
      <div class="axis-block">
        <label
          >Split by
          <span class="toggle-hint">the 15 most common values</span></label
        >
        <column-combobox
          .columns=${this.colorByPool().filter((c) => c.name !== s.box_column)}
          .value=${s.box_group_column}
          placeholder="Column to split by…"
          .select=${(name: string) => setBoxGroupColumn(name)}
        ></column-combobox>
      </div>
    `;
  }

  /* ── Pie inputs ───────────────────────────────────────── */

  /**
   * Two readings of "share of a whole", behind one switch.
   *
   * By signal: the picker's order is the slice order, and dragging it redraws
   * without a query. By band: one signal, cut on round numbers unless cut points
   * are typed. Every control here that changes what is counted waits for Plot;
   * the donut/pie shape lives in the gear because it only redraws.
   */
  private renderPieInputs(s: PlotState): TemplateResult {
    const bands = s.pie_mode === 'bands';
    return html`
      <div class="axis-block">
        <label>Slices</label>
        <div class="seg">
          <button
            type="button"
            class="seg-btn ${bands ? '' : 'active'}"
            aria-pressed=${bands ? 'false' : 'true'}
            title="One slice per signal - how a total splits across them"
            @click=${() => setPieMode('signals')}
          >
            By signal
          </button>
          <button
            type="button"
            class="seg-btn ${bands ? 'active' : ''}"
            aria-pressed=${bands ? 'true' : 'false'}
            title="One signal cut into value bands - how much of the time it spent in each"
            @click=${() => setPieMode('bands')}
          >
            By band
          </button>
        </div>
      </div>
      ${bands ? this.renderPieBandInputs(s) : this.renderPieSignalInputs(s)}
    `;
  }

  private renderPieSignalInputs(s: PlotState): TemplateResult {
    const count = s.pie_columns.length;
    const full = count >= PIE_MAX_COLUMNS;
    return html`
      <div class="axis-block">
        <label
          >Signals
          <span class="toggle-hint">${count < 2 ? 'two or more' : `${count} slices`}</span></label
        >
        <series-picker
          .items=${s.pie_columns}
          .pool=${this.plottableNumeric()}
          addPlaceholder=${full ? `Limit is ${PIE_MAX_COLUMNS} slices` : '+ Add signal…'}
          .colorOf=${(name: string, i: number) => seriesColorAt(s, name, i)}
          .addItem=${(name: string) => addPieCol(name)}
          .removeItem=${(name: string) => removePieCol(name)}
          .reorder=${(from: number, to: number) => reorderPieCol(from, to)}
        ></series-picker>
      </div>
      <div class="axis-block">
        <label>Slice size</label>
        <plain-select
          id="pie-agg"
          .value=${s.pie_agg}
          .options=${PIE_AGGS.map((a) => ({ value: a.value, label: a.label }))}
          .select=${(v: string) => setPieAgg(v as PieAgg)}
        ></plain-select>
        <div class="toggle-hint">
          ${s.pie_agg === 'avg'
            ? 'Each signal averaged over the range - power split, load split.'
            : 'Each signal added up over every row - only a total if rows are evenly spaced.'}
        </div>
      </div>
    `;
  }

  private renderPieBandInputs(s: PlotState): TemplateResult {
    const d = lastPlotData.value;
    const drawn =
      d && d.plot_type === 'pie' && d.mode === 'bands' && d.column === s.pie_band_column ? d : null;
    const typed = !!s.pie_band_edges?.length;
    const autoCuts = drawn?.edges_auto && drawn.edges.length ? drawn.edges.join(', ') : '';
    return html`
      <div class="axis-block">
        <label>Signal</label>
        <column-combobox
          .columns=${this.plottableNumeric()}
          .value=${s.pie_band_column}
          placeholder="Signal to cut into bands…"
          .select=${(name: string) => {
            this.pieEdgeError = null;
            setPieBandColumn(name);
          }}
        ></column-combobox>
      </div>
      <div class="axis-block">
        <label
          >Bands
          <span class="toggle-hint"
            >${typed ? `${s.pie_band_edges!.length + 1} from cut points` : `about ${s.pie_bands}, on round numbers`}</span
          ></label
        >
        <div class="heat-bins-row">
          <input
            type="range"
            min=${PIE_BANDS_MIN}
            max=${PIE_BANDS_MAX}
            step="1"
            ?disabled=${typed}
            title=${typed ? 'Clear the cut points to choose a band count' : 'About how many bands'}
            .value=${String(s.pie_bands)}
            @input=${(e: Event) => setPieBands(Number((e.target as HTMLInputElement).value))}
          />
          <span class="mono-val">${typed ? '-' : s.pie_bands}</span>
        </div>
      </div>
      <div class="axis-block">
        <label>Cut points <span class="toggle-hint">optional</span></label>
        <input
          type="text"
          class="cell-size-input"
          inputmode="decimal"
          placeholder=${autoCuts ? `auto: ${autoCuts}` : 'auto - e.g. 25, 50, 75'}
          .value=${typed ? s.pie_band_edges!.join(', ') : ''}
          title="Values where one band ends and the next begins. The first and last bands are open-ended, so every row lands in one."
          @change=${(e: Event) => {
            const { edges, error } = parseBandEdges((e.target as HTMLInputElement).value);
            this.pieEdgeError = error;
            if (!error) setPieBandEdges(edges);
          }}
        />
        ${this.pieEdgeError ? html`<span class="field-error">${this.pieEdgeError}</span>` : nothing}
      </div>
      <div class="axis-block">
        <label
          >Colour
          <span class="toggle-hint">${s.color_scale}</span></label
        >
        ${this.renderRampSwatches(s)}
      </div>
    `;
  }

  private renderHistInputs(s: PlotState): TemplateResult {
    return html`
      <div class="axis-block">
        <label>Columns <span>(overlay)</span></label>
        <series-picker
          .items=${s.hist_columns}
          .pool=${this.plottableNumeric()}
          addPlaceholder="+ Add column…"
          .colorOf=${(name: string, i: number) => seriesColorAt(s, name, i)}
          .addItem=${(name: string) => addHistCol(name)}
          .removeItem=${(name: string) => removeHistCol(name)}
          .reorder=${(from: number, to: number) => reorderHistCol(from, to)}
        ></series-picker>
      </div>
      <div class="axis-block">
        <label>Bins <span class="mono-val">${s.hist_bins}</span></label>
        <input
          type="range"
          min=${HIST_BINS_MIN}
          max=${HIST_BINS_MAX}
          step="1"
          .value=${String(s.hist_bins)}
          @input=${(e: Event) => setHistBins(Number((e.target as HTMLInputElement).value))}
          @change=${() => {
            if (plotState.value.hist_columns.length) doPlot();
          }}
        />
      </div>
    `;
  }

  /* ── Overlays (line only, conditional) ────────────────── */

  private renderOverlays(s: PlotState): TemplateResult {
    if (s.plot_type !== 'line') return html`${nothing}`;
    const names = new Set(allColumns.value.map((c) => c.name));
    const hasLegCols = names.has('leg_key') && names.has('is_reliable');
    const hasChangeover = allColumns.value.some((c) =>
      /^__DG\d+_CHANGEOVER_PERIOD$/.test(c.name),
    );
    if (!hasLegCols && !hasChangeover) return html`${nothing}`;
    // Leg and changeover bands are per-ship. Stacked on a single overlay they
    // read as mush with no owner, so they are unavailable *there* - grid gives
    // each panel its own bands, so they stay available in that view.
    const multi = isMultiShip() && s.view_mode !== 'grid';
    const offReason = multi
      ? 'Unavailable while several ships share one chart - switch to Grid for per-ship bands'
      : undefined;
    // Both overlays are time spans, and the renderer only draws them on a time
    // axis - plot SOG against power and they are silently absent. Say so where
    // the toggle is rather than leaving someone to wonder why nothing appeared.
    const notTime = !isTimeAxis(s);
    const timeWarning = (what: string): TemplateResult => html`<span
      class="toggle-hint warn"
      title=${`${what} are time spans. The X axis is '${s.x_column}', so they cannot be placed - switch X back to ${timeColumn()} to see them.`}
      >needs a time X axis</span
    >`;
    return html`
      ${hasLegCols
        ? html`<div class="plot-toggle-row ${multi ? 'unavailable' : ''}">
            <label class="inline-label" title=${offReason ?? nothing}>
              <input
                type="checkbox"
                ?disabled=${multi}
                .checked=${s.show_legs && !multi}
                @change=${(e: Event) => setShowLegs((e.target as HTMLInputElement).checked)}
              />
              Leg overlay
            </label>
            ${multi ? html`<span class="toggle-hint">grid view only</span>` : nothing}
            ${!multi && s.show_legs && notTime ? timeWarning('Legs') : nothing}
            ${legsLoading.value
              ? html`<span class="spinner overlay-spinner" title="Loading legs…"></span>`
              : nothing}
            <leg-search></leg-search>
          </div>`
        : nothing}
      ${hasChangeover
        ? html`<div class="plot-toggle-row ${multi ? 'unavailable' : ''}">
            <label class="inline-label" title=${offReason ?? nothing}>
              <input
                type="checkbox"
                ?disabled=${multi}
                .checked=${s.show_changeovers && !multi}
                @change=${(e: Event) =>
                  setShowChangeovers((e.target as HTMLInputElement).checked)}
              />
              Changeover overlay
            </label>
            ${multi ? html`<span class="toggle-hint">grid view only</span>` : nothing}
            ${!multi && s.show_changeovers && notTime ? timeWarning('Changeovers') : nothing}
            ${changeoversLoading.value
              ? html`<span class="spinner overlay-spinner" title="Loading changeovers…"></span>`
              : nothing}
          </div>`
        : nothing}
    `;
  }

  /* ── Render ───────────────────────────────────────────── */

  override render(): TemplateResult {
    const s = plotState.value;
    return html`
      <div class="card" data-tab="plot">
        <h2>Plot<span class="req-mark" title="Required - pick what to plot">*</span></h2>
        ${this.renderTypeGrid(s)}
        <div class="plot-inputs">
          ${s.plot_type === 'line' ? this.renderLineInputs(s) : nothing}
          ${s.plot_type === 'scatter' ? this.renderScatterInputs(s) : nothing}
          ${s.plot_type === 'heatmap' ? this.renderHeatmapInputs(s) : nothing}
          ${s.plot_type === 'correlation' ? this.renderCorrelationInputs(s) : nothing}
          ${s.plot_type === 'pie' ? this.renderPieInputs(s) : nothing}
          ${s.plot_type === 'box' ? this.renderBoxInputs(s) : nothing}
          ${s.plot_type === 'number' ? this.renderNumberInputs(s) : nothing}
          ${s.plot_type === 'table' ? html`<table-controls></table-controls>` : nothing}
          ${s.plot_type === 'gauge' ? html`<gauge-controls></gauge-controls>` : nothing}
          ${s.plot_type === 'sankey' ? html`<sankey-controls></sankey-controls>` : nothing}
          ${s.plot_type === 'histogram' ? this.renderHistInputs(s) : nothing}
          ${s.plot_type === 'map' ? this.renderMapInputs(s) : nothing}
        </div>
        <axis-titles></axis-titles>
        ${s.plot_type === 'heatmap' ||
        s.plot_type === 'correlation' ||
        s.plot_type === 'pie' ||
        s.plot_type === 'box' ||
        s.plot_type === 'table'
          ? nothing
          : this.renderAdvanced(s)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plot-controls': PlotControls;
  }
}
